import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Image from 'react-bootstrap/Image';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL, api } from '../config/api';
import { getUserDetailsHome, updateUser } from '../services/userService';
import ListPageLayout from './ui/ListPageLayout';
import DataTable from './ui/DataTable';
import AppModal from './ui/AppModal';
import ActionIcon from './ui/ActionIcon';
import StatusBadge from './ui/StatusBadge';
import { useLayout } from '../layout/AppLayout';
import { showError, showSuccess, showInfo } from '../utils/toast';
import { getApiErrorMessage } from '../utils/apiError';

const ROLE_FILTERS = [
  { value: '', label: 'All Users' },
  { value: 'User', label: 'Recruiters' },
  { value: 'TeamLead', label: 'Team Leads' },
  { value: 'Admin', label: 'Admins' },
];

const USER_STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'InActive', label: 'Inactive' },
];

function Users() {
  return (
    <ListPageLayout
      title="User List"
      breadcrumbs={[
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Users', path: '/users' },
        { label: 'User List' },
      ]}
      backTo="/users"
      actions={(
        <UserListActions />
      )}
    >
      <UserListSection />
    </ListPageLayout>
  );
}

function UserListActions() {
  const { openFormModal } = useLayout();
  return (
    <Button variant="primary" className="hr-btn-primary" onClick={() => openFormModal('create-user')}>
      + Create User
    </Button>
  );
}

export function UserListSection({ roleFilter: externalRoleFilter, onRoleFilterChange }) {
  const { tableRefresh, openFormModal } = useLayout();
  const [searchParams, setSearchParams] = useSearchParams();
  const [userDetailsList, setUserDetailsList] = useState([]);
  const [showUserData, setShowUserData] = useState(null);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [updatingStatusId, setUpdatingStatusId] = useState('');

  const getUserList = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await getUserDetailsHome();
      setUserDetailsList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUserDetailsList([]);
      setLoadError(getApiErrorMessage(error, 'Failed to load users.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getUserList();
  }, [getUserList, tableRefresh.users]);

  useEffect(() => {
    if (externalRoleFilter !== undefined) {
      setRoleFilter(externalRoleFilter);
      return;
    }
    const role = searchParams.get('role') || '';
    setRoleFilter(role);
  }, [searchParams, externalRoleFilter]);

  const handleRoleFilter = (value) => {
    setRoleFilter(value);
    if (onRoleFilterChange) onRoleFilterChange(value);
    if (externalRoleFilter === undefined) {
      if (value) setSearchParams({ role: value });
      else setSearchParams({});
    }
  };

  const deleteUserDetails = useCallback(async (id) => {
    const confirmation = window.confirm('Are you sure you want to delete this user?');
    if (!confirmation) return;

    try {
      const { data: JSOData } = await api.delete(`/deleteUser/${id}`);

      if (JSOData.status === 'success') {
        showSuccess(JSOData.msg);
        await getUserList();
      } else {
        showError(JSOData.msg || 'Failed to delete user. Please try again.');
      }
    } catch (error) {
      showError(getApiErrorMessage(error, 'Failed to delete user. Please try again.'));
    }
  }, [getUserList]);

  const readUserDatafun = async (id) => {
    try {
      const { data: JSOData } = await api.get(`/getUserData/${id}`);
      const { userDetails, teamDetails } = JSOData;
      setShowUserData({ userDetails, teamDetails });
      setShow(true);
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const handleStatusChange = useCallback(async (row, nextStatus) => {
    const currentStatus = row.Status || 'Active';
    if (nextStatus === currentStatus) return;

    if (nextStatus === 'InActive') {
      const confirmed = window.confirm(
        `Mark ${row.EmployeeName} as Inactive? They will be removed from team assignment lists and cannot log in.`
      );
      if (!confirmed) return;
    }

    setUpdatingStatusId(row._id);
    try {
      await updateUser(row._id, {
        name: row.EmployeeName,
        Code: row.EmpCode,
        email: row.Email,
        status: nextStatus,
        usertype: row.UserType,
        profile: row.ProfilePic || '',
      });

      setUserDetailsList((prev) => prev.map((user) => (
        user._id === row._id ? { ...user, Status: nextStatus } : user
      )));
      showSuccess(nextStatus === 'InActive' ? 'User marked as inactive.' : 'User marked as active.');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Failed to update user status.'));
    } finally {
      setUpdatingStatusId('');
    }
  }, []);

  const tableData = useMemo(() => {
    if (!roleFilter) return userDetailsList;
    return userDetailsList.filter((item) => item.UserType === roleFilter);
  }, [userDetailsList, roleFilter]);

  const columns = useMemo(() => [
    { key: 'EmpCode', label: 'Employee Code', sortable: true },
    { key: 'EmployeeName', label: 'Employee Name', sortable: true },
    { key: 'Email', label: 'Email', sortable: true },
    { key: 'UserType', label: 'User Type', sortable: true },
    {
      key: 'Status',
      label: 'Status',
      sortable: true,
      render: (row) => (
        <Form.Select
          size="sm"
          className={`hr-user-status-select ${row.Status === 'InActive' ? 'is-inactive' : 'is-active'}`}
          value={row.Status === 'InActive' ? 'InActive' : 'Active'}
          disabled={updatingStatusId === row._id}
          aria-label={`Status for ${row.EmployeeName}`}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => handleStatusChange(row, event.target.value)}
        >
          {USER_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Form.Select>
      ),
    },
    { key: 'CreatedBy', label: 'Initiated By', sortable: true },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="hr-table-actions">
          <ActionIcon variant="view" src="/Images/view.svg" alt="View user" onClick={() => readUserDatafun(row._id)} />
          <ActionIcon variant="edit" src="/Images/edit.svg" alt="Edit user" onClick={() => openFormModal('edit-user', { id: row._id })} />
          <ActionIcon variant="delete" src="/Images/trash.svg" alt="Delete user" onClick={() => deleteUserDetails(row._id)} />
        </div>
      ),
    },
  ], [deleteUserDetails, handleStatusChange, openFormModal, updatingStatusId]);

  const handleImportRows = (rows) => {
    showInfo(`Imported ${rows.length} row(s) from file. Use Create User to add new records.`);
  };

  return (
    <>
      {loadError && <div className="alert alert-danger">{loadError}</div>}

      {!loading && !loadError && (
        <p className="hr-list-summary">
          Showing <strong>{tableData.length}</strong> of <strong>{userDetailsList.length}</strong> users
        </p>
      )}

      <div className="hr-role-filters">
        {ROLE_FILTERS.map((filter) => (
          <button
            key={filter.value || 'all'}
            type="button"
            className={`hr-role-filter-btn ${roleFilter === filter.value ? 'active' : ''}`}
            onClick={() => handleRoleFilter(filter.value)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={tableData}
        searchPlaceholder="Search by employee name, email, or code"
        searchKeys={['EmployeeName', 'Email', 'EmpCode']}
        onRefresh={getUserList}
        onImportRows={handleImportRows}
        exportFileName="users-export.csv"
        loading={loading}
        emptyMessage="No users found."
      />

      <AppModal
        show={show}
        onHide={() => setShow(false)}
        title="User Details"
        subtitle={showUserData?.userDetails ? `${showUserData.userDetails.EmployeeName} (${showUserData.userDetails.EmpCode})` : ''}
      >
        {showUserData && showUserData.userDetails && (
          <>
            <div className="text-center mb-4">
              {showUserData.userDetails.ProfilePic ? (
                <Image
                  roundedCircle
                  style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                  src={`${API_BASE_URL}${showUserData.userDetails.ProfilePic.startsWith('/') ? '' : '/'}${showUserData.userDetails.ProfilePic}`}
                  alt="Profile"
                />
              ) : (
                <p>No profile picture available.</p>
              )}
            </div>

            <div className="hr-detail-grid mb-4">
              <div className="hr-detail-item">
                <label>Email</label>
                <span>{showUserData.userDetails.Email || 'N/A'}</span>
              </div>
              <div className="hr-detail-item">
                <label>User Type</label>
                <span>{showUserData.userDetails.UserType || 'N/A'}</span>
              </div>
              <div className="hr-detail-item">
                <label>Status</label>
                <span><StatusBadge status={showUserData.userDetails.Status || 'N/A'} /></span>
              </div>
            </div>

            <h5 className="hr-section-title">
              <img src="/Images/icon.png" alt="" />
              Team Details
            </h5>

            {showUserData.userDetails.UserType === 'TeamLead' ? (
              <table className="table table-bordered hr-table">
                <thead>
                  <tr>
                    <th>Emp Code</th>
                    <th>Employee Name</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {showUserData.teamDetails && showUserData.teamDetails.filter((member) => member.Status !== 'InActive').length > 0 ? (
                    showUserData.teamDetails
                      .filter((member) => member.Status !== 'InActive')
                      .map((teamMember) => (
                      <tr key={teamMember._id}>
                        <td>{teamMember.EmpCode || 'N/A'}</td>
                        <td>{teamMember.EmployeeName || 'N/A'}</td>
                        <td>{teamMember.Email || 'N/A'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="hr-empty-state">No team members assigned.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <p className="hr-empty-state">Team details are only available for Team Lead users.</p>
            )}
          </>
        )}
      </AppModal>
    </>
  );
}

export default Users;
